import React, { useState, useEffect } from "react";
import { supabase } from "../utils/supabaseClient";
import { ToastContainer, toast } from "react-toastify";
import { Calendar, Clock } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import "react-toastify/dist/ReactToastify.css";
import "../styles/WebinarManagement.css";

const WebinarManagement = () => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentWebinar, setCurrentWebinar] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    from_time: "",
    to_time: "",
    registration_link: "",
    brochure_link: "",
    product_info_link: "",
  });

  useEffect(() => {
    fetchWebinars();
  }, []);

  const fetchWebinars = async () => {
    try {
      const { data, error } = await supabase
        .from("webinars")
        .select("*")
        .order("date", { ascending: false });

      if (error) throw error;
      setWebinars(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        const { error } = await supabase
          .from("webinars")
          .update({
            title: formData.title,
            description: formData.description,
            date: formData.date,
            from_time: formData.from_time,
            to_time: formData.to_time,
            registration_link: formData.registration_link,
            brochure_link: formData.brochure_link,
            product_info_link: formData.product_info_link,
          })
          .eq("id", currentWebinar.id);

        if (error) throw error;
        toast.success("Webinar updated successfully!");
      } else {
        const { error } = await supabase.from("webinars").insert([
          {
            title: formData.title,
            description: formData.description,
            date: formData.date,
            from_time: formData.from_time,
            to_time: formData.to_time,
            registration_link: formData.registration_link,
            brochure_link: formData.brochure_link,
            product_info_link: formData.product_info_link,
          },
        ]);

        if (error) throw error;
        toast.success("Webinar created successfully!");
      }

      setFormData({
        title: "",
        description: "",
        date: "",
        from_time: "",
        to_time: "",
        registration_link: "",
        brochure_link: "",
        product_info_link: "",
      });
      setIsEditing(false);
      setCurrentWebinar(null);
      fetchWebinars();
    } catch (error) {
      setError(error.message);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleEdit = (webinar) => {
    setCurrentWebinar(webinar);
    setFormData(webinar);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this webinar?")) {
      try {
        const { error } = await supabase.from("webinars").delete().eq("id", id);

        if (error) throw error;
        toast.success("Webinar deleted successfully!");
        fetchWebinars();
      } catch (error) {
        setError(error.message);
        toast.error("An error occurred while deleting the webinar.");
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      description: "",
      date: "",
      from_time: "",
      to_time: "",
      registration_link: "",
      brochure_link: "",
      product_info_link: "",
    });
    setIsEditing(false);
    setCurrentWebinar(null);
  };

  const formatTime = (time) => {
    try {
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minutes} ${ampm}`;
    } catch (error) {
      return time;
    }
  };

  if (loading) return <LoadingSpinner size="large" />;
  if (error)
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );

  return (
    <div className="page-content">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <h2>{isEditing ? "Edit Webinar" : "Add New Webinar"}</h2>

      <form onSubmit={handleSubmit} className="webinar-form">
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Date</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="from_time">From Time</label>
            <input
              type="time"
              id="from_time"
              name="from_time"
              value={formData.from_time}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="to_time">To Time</label>
            <input
              type="time"
              id="to_time"
              name="to_time"
              value={formData.to_time}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="registration_link">Registration Link</label>
          <input
            type="url"
            id="registration_link"
            name="registration_link"
            value={formData.registration_link}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="brochure_link">Brochure Link</label>
          <input
            type="url"
            id="brochure_link"
            name="brochure_link"
            value={formData.brochure_link}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="product_info_link">Product Information Link</label>
          <input
            type="url"
            id="product_info_link"
            name="product_info_link"
            value={formData.product_info_link}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button">
            {isEditing ? "Update Webinar" : "Add Webinar"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              className="cancel-button"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="webinars-list">
        <h3>Existing Webinars</h3>
        <div className="webinars-grid">
          {webinars.map((webinar) => (
            <div key={webinar.id} className="webinar-card">
              <div className="webinar-card-content">
                <h4>{webinar.title}</h4>
                <p>{webinar.description}</p>
                <div className="details-container">
                  <div className="detail-item">
                    <Calendar className="detail-icon" />
                    <span>{new Date(webinar.date).toLocaleDateString()}</span>
                  </div>
                  <div className="detail-item">
                    <Clock className="detail-icon" />
                    <span>
                      {formatTime(webinar.from_time)} -{" "}
                      {formatTime(webinar.to_time)}
                    </span>
                  </div>
                </div>
                <div className="webinar-actions">
                  <button
                    onClick={() => handleEdit(webinar)}
                    className="edit-button"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(webinar.id)}
                    className="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WebinarManagement;
